import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Card from '@mui/material/Card';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../services/api';
import Chip from '@mui/material/Chip';
import { Search, Eye, X, Mail, Phone, User, Users, FileText, TrendingUp, Briefcase, Euro } from 'lucide-react';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import Badge from '@mui/material/Badge';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Autocomplete from '@mui/material/Autocomplete';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';

function CircularProgressWithLabel(props) {
  const [progress, setProgress] = React.useState(10);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prevProgress) => (prevProgress >= 90 ? 90 : prevProgress + 10));
    }, 200);
    return () => {
      clearInterval(timer);
    };
  }, []);

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CircularProgress variant="determinate" {...props} value={progress} />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="caption" component="div" color="text.secondary">
          {`${Math.round(progress)}%`}
        </Typography>
      </Box>
    </Box>
  );
}

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

const MONEY_COLOR = '#059669';

const getDossierStatusLabel = (status) => {
  if (!status) return '';
  const map = {
    documents_sent: 'Documenti inviati',
    failed: 'Fallito',
    'in negotiation': 'In trattativa',
    processing: 'In lavorazione',
    submitted_quote: 'Preventivo inviato',
    terminated: 'Terminato',
  };
  return map[status] || status;
};

export default function Dossier() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('id');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterContractType, setFilterContractType] = useState('');
  const [filterRespComm, setFilterRespComm] = useState('');
  const [filterRespCont, setFilterRespCont] = useState('');
  const [filterRespUpsell, setFilterRespUpsell] = useState('');
  const [filterStatoPreventivo, setFilterStatoPreventivo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [userNames, setUserNames] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [contractTypeOptions, setContractTypeOptions] = useState([]);
  const [statiPreventivoOptions, setStatiPreventivoOptions] = useState([]);
  const [contractTypeStats, setContractTypeStats] = useState([]);
  const [loadingContractTypeStats, setLoadingContractTypeStats] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState({
    id: false,
    responsabile: true,
    acconto: true,
    dataConferimento: true,
  });
  const [columnMenuAnchorEl, setColumnMenuAnchorEl] = useState(null);
  const [openContactsModal, setOpenContactsModal] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [selectedDossierRef, setSelectedDossierRef] = useState('');
  const [selectedDossierCustomer, setSelectedDossierCustomer] = useState(null);

  const handleContactsClick = async (e, dossier) => {
    e.stopPropagation();
    setOpenContactsModal(true);
    setLoadingContacts(true);
    setSelectedDossierRef(dossier.reference);

    const customerName =
      dossier.customer_ragione_sociale ||
      ((dossier.customer_name || dossier.customer_surname)
        ? `${dossier.customer_name || ''} ${dossier.customer_surname || ''}`.trim()
        : dossier.rif_customer);

    setSelectedDossierCustomer({
      name: customerName || '',
      email: dossier.customer_email || '',
      telephone: dossier.customer_telephone || '',
    });

    try {
      const res = await api.get(`/contatto/dossier/${dossier.id}`);
      setSelectedContacts(res.data);
    } catch (error) {
      console.error('Error fetching contacts for dossier:', error);
      toast.error('Errore nel caricamento dei contatti');
      setSelectedContacts([]);
    } finally {
      setLoadingContacts(false);
    }
  };

  const handleCloseContactsModal = () => {
    setOpenContactsModal(false);
    setSelectedContacts([]);
    setSelectedDossierRef('');
    setSelectedDossierCustomer(null);
  };

  const handleOpenColumnMenu = (event) => {
    setColumnMenuAnchorEl(event.currentTarget);
  };

  const handleCloseColumnMenu = () => {
    setColumnMenuAnchorEl(null);
  };

  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const buildFilterParams = () => {
    const params = {};
    if (filterSearch) params.search = filterSearch;
    if (filterStatus) params.status = filterStatus;
    if (filterContractType) params.contractType = filterContractType;
    if (filterRespComm) params.respComm = filterRespComm;
    if (filterRespCont) params.respCont = filterRespCont;
    if (filterRespUpsell) params.respUpsell = filterRespUpsell;
    if (filterStatoPreventivo) params.statoPreventivo = filterStatoPreventivo;
    return params;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        orderBy,
        order,
        ...buildFilterParams(),
      };

      const res = await api.get('/dossier', { params });
      setList(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error('Error fetching dossier list:', error);
      toast.error('Errore nel caricamento dei dossier');
    } finally {
      setLoading(false);
    }
  };

  const fetchContractTypeStats = async () => {
    setLoadingContractTypeStats(true);
    try {
      const params = buildFilterParams();
      const res = await api.get('/dossier/stats/contract-type', { params });
      setContractTypeStats(res.data.items || []);
    } catch (error) {
      console.error('Error fetching dossier contract type stats:', error);
    } finally {
      setLoadingContractTypeStats(false);
    }
  };

  const optionalColumnsActive =
    columnVisibility.id ||
    columnVisibility.responsabile ||
    columnVisibility.acconto ||
    columnVisibility.dataConferimento;

  const baseColumnsCount = 5;
  const extraColumnsCount =
    (columnVisibility.id ? 1 : 0) +
    (columnVisibility.responsabile ? 1 : 0) +
    (columnVisibility.acconto ? 1 : 0) +
    (columnVisibility.dataConferimento ? 1 : 0);
  const totalColumns = baseColumnsCount + extraColumnsCount;

  useEffect(() => {
    fetchData();
  }, [
    page,
    rowsPerPage,
    order,
    orderBy,
    filterSearch,
    filterStatus,
    filterContractType,
    filterRespComm,
    filterRespCont,
    filterRespUpsell,
    filterStatoPreventivo,
  ]);

  useEffect(() => {
    fetchContractTypeStats();
  }, [
    filterSearch,
    filterStatus,
    filterContractType,
    filterRespComm,
    filterRespCont,
    filterRespUpsell,
    filterStatoPreventivo,
  ]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await api.get('/lorenzo-users');
        const names = res.data
          .map((u) => [u.name, u.surname].filter(Boolean).join(' '))
          .filter((n) => n);
        setUserNames([...new Set(names)].sort());
      } catch (error) {
        console.error('Error fetching users for dossier filters:', error);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const res = await api.get('/dossier/filters/options');
        const data = res.data || {};
        setStatusOptions((data.statuses || []).filter(Boolean));
        setContractTypeOptions((data.contractTypes || []).filter(Boolean));
        setStatiPreventivoOptions((data.statiPreventivo || []).filter(Boolean));
      } catch (error) {
        console.error('Error fetching dossier filter options:', error);
      }
    };
    fetchFilterOptions();
  }, []);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterBySearch = (event) => {
    setFilterSearch(event.target.value);
    setPage(0);
  };

  const handleFilterByStatus = (event) => {
    setFilterStatus(event.target.value);
    setPage(0);
  };

  const handleFilterByContractType = (event) => {
    setFilterContractType(event.target.value);
    setPage(0);
  };
  
  const handleFilterByStatoPreventivo = (event) => {
    setFilterStatoPreventivo(event.target.value);
    setPage(0);
  };

  const handleView = (id) => {
    navigate(`/dossier/${id}`);
  };

  const getStatusColor = (status) => {
    if (!status) return 'default';
    const lower = status.toLowerCase();
    
    // Success (Green)
    if (['completato', 'confermato', 'risolto', 'pagato', 'attivo', 'success'].some(k => lower.includes(k))) return 'success';
    
    // Error (Red)
    if (['errore', 'annullato', 'bloccato', 'rifiutato', 'chiuso', 'fallito'].some(k => lower.includes(k))) return 'error';
    
    // Warning (Orange)
    if (['in attesa', 'pending', 'da verificare', 'sospeso', 'da contattare', 'richiamare'].some(k => lower.includes(k))) return 'warning';
    
    // Info (Blue)
    if (['nuovo', 'bozza', 'in lavorazione', 'aperto', 'regolare'].some(k => lower.includes(k))) return 'info';
    
    return 'default';
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const activeFiltersCount = [filterStatus, filterContractType, filterRespComm, filterRespCont, filterRespUpsell, filterStatoPreventivo].filter(Boolean).length;

  const totalByContractType = contractTypeStats.reduce((sum, item) => sum + (item.count || 0), 0);

  const getContractTypeVisuals = (contractType) => {
    const label = (contractType || '').toLowerCase();

    if (label.includes('upsell')) {
      return {
        icon: TrendingUp,
        iconBg: 'rgba(16, 185, 129, 0.08)',
        iconColor: '#059669',
        borderColor: 'rgba(16, 185, 129, 0.18)',
      };
    }

    if (label.includes('contratto') || label.includes('contrattuale')) {
      return {
        icon: FileText,
        iconBg: 'rgba(59, 130, 246, 0.08)',
        iconColor: '#2563EB',
        borderColor: 'rgba(59, 130, 246, 0.18)',
      };
    }

    return {
      icon: Briefcase,
      iconBg: 'rgba(148, 163, 184, 0.1)',
      iconColor: '#475569',
      borderColor: 'rgba(148, 163, 184, 0.25)',
    };
  };

  const PercentageDonut = ({ value, color }) => {
    const safeValue = Number.isFinite(value) ? value : 0;
    const clamped = Math.max(0, Math.min(100, safeValue));

    return (
      <Box
        sx={{
          position: 'relative',
          width: 56,
          height: 56,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress
          variant="determinate"
          value={100}
          size={56}
          thickness={5}
          sx={{ color: 'rgba(148, 163, 184, 0.2)' }}
        />
        <CircularProgress
          variant="determinate"
          value={clamped}
          size={56}
          thickness={5}
          sx={{
            color,
            position: 'absolute',
            left: 0,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {`${clamped.toFixed(1)}%`}
          </Typography>
        </Box>
      </Box>
    );
  };

  const sortedList = [...list].sort((a, b) => {
    const isAsc = order === 'asc';
    if (orderBy === 'id' || orderBy === 'importo_totale' || orderBy === 'acconto') {
      return isAsc ? a[orderBy] - b[orderBy] : b[orderBy] - a[orderBy];
    }
    const valA = a[orderBy] ? a[orderBy].toString().toLowerCase() : '';
    const valB = b[orderBy] ? b[orderBy].toString().toLowerCase() : '';
    if (valA < valB) return isAsc ? -1 : 1;
    if (valA > valB) return isAsc ? 1 : -1;
    return 0;
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FileText size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Fascicoli
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Elenco fascicoli con contatti, pratiche e importi aggregati
            </Typography>
          </Box>
        </Box>
      </Box>

      {loadingContractTypeStats ? (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={32} />
        </Box>
      ) : contractTypeStats.length > 0 ? (
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          {contractTypeStats.map((stat) => {
            const percentage = totalByContractType > 0 ? (stat.count * 100) / totalByContractType : 0;
            const visuals = getContractTypeVisuals(stat.contractType);
            const Icon = visuals.icon;
            const isActive = filterContractType === stat.contractType;
            const clampedPercentage = Math.max(0, Math.min(100, percentage));

            return (
              <Card
                key={stat.contractType}
                onClick={() => {
                  setFilterContractType(stat.contractType || '');
                  setShowFilters(true);
                  setPage(0);
                }}
                sx={{
                  flex: '1 1 220px',
                  borderRadius: 2,
                  cursor: 'pointer',
                  boxShadow: isActive
                    ? '0 0 0 1px rgba(37, 99, 235, 0.35), 0 12px 24px -4px rgba(15, 23, 42, 0.28)'
                    : '0 0 2px 0 rgba(145, 158, 171, 0.15), 0 12px 24px -6px rgba(145, 158, 171, 0.18)',
                  border: `1px solid ${isActive ? visuals.iconColor : visuals.borderColor}`,
                  transition: 'all 0.15s ease-in-out',
                  '&:hover': {
                    boxShadow:
                      '0 0 0 1px rgba(148, 163, 184, 0.35), 0 16px 32px -6px rgba(15, 23, 42, 0.28)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <Box
                  sx={{
                    p: 2.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, flexGrow: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {stat.contractType}
                    </Typography>
                    <Typography variant="h6">
                      {stat.count} fascicoli
                    </Typography>
                    {stat.total_importo > 0 && (
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: 'text.primary' }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Euro size={16} color={visuals.iconColor} />
                          <span>
                            Importo totale:{' '}
                            {Number(stat.total_importo).toLocaleString('it-IT', {
                              style: 'currency',
                              currency: 'EUR',
                            })}
                          </span>
                        </Box>
                      </Typography>
                    )}
                  </Box>
                  <PercentageDonut value={clampedPercentage} color={visuals.iconColor} />
                </Box>
              </Card>
            );
          })}
        </Box>
      ) : null}

      <StyledCard>
        <Grid container spacing={2} sx={{ mb: 3, p: 2 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              placeholder="Cerca per reference, descrizione, ID..."
              value={filterSearch}
              onChange={handleFilterBySearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={20} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {filterSearch && (
                        <IconButton size="small" onClick={() => setFilterSearch('')}>
                          <X size={20} />
                        </IconButton>
                      )}
                      <IconButton onClick={() => setShowFilters(!showFilters)}>
                        <Badge badgeContent={activeFiltersCount} color="primary">
                          <FilterAltIcon color={showFilters || activeFiltersCount > 0 ? 'primary' : 'action'} />
                        </Badge>
                      </IconButton>
                      <Tooltip title="Seleziona colonne aggiuntive">
                        <IconButton onClick={handleOpenColumnMenu}>
                          <ViewColumnIcon color={optionalColumnsActive ? 'primary' : 'action'} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          
          {showFilters && (
            <>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filterStatus}
                    label="Status"
                    onChange={handleFilterByStatus}
                  >
                    <MenuItem value=""><em>Tutti</em></MenuItem>
                    {statusOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {getDossierStatusLabel(option)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Tipo Contratto</InputLabel>
                  <Select
                    value={filterContractType}
                    label="Tipo Contratto"
                    onChange={handleFilterByContractType}
                  >
                    <MenuItem value=""><em>Tutti</em></MenuItem>
                    {contractTypeOptions.map((option) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Stato Preventivo</InputLabel>
                  <Select
                    value={filterStatoPreventivo}
                    label="Stato Preventivo"
                    onChange={handleFilterByStatoPreventivo}
                  >
                    <MenuItem value=""><em>Tutti</em></MenuItem>
                    {statiPreventivoOptions.map((option) => (
                      <MenuItem key={option} value={option}>{option}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={userNames}
                  value={filterRespComm || null}
                  onChange={(event, newValue) => {
                    setFilterRespComm(newValue || '');
                    setPage(0);
                  }}
                  renderInput={(params) => <TextField {...params} label="Resp. Commerciale" />}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={userNames}
                  value={filterRespCont || null}
                  onChange={(event, newValue) => {
                    setFilterRespCont(newValue || '');
                    setPage(0);
                  }}
                  renderInput={(params) => <TextField {...params} label="Resp. Contabile" />}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={userNames}
                  value={filterRespUpsell || null}
                  onChange={(event, newValue) => {
                    setFilterRespUpsell(newValue || '');
                    setPage(0);
                  }}
                  renderInput={(params) => <TextField {...params} label="Resp. Upsell" />}
                  fullWidth
                />
              </Grid>
            </>
          )}
        </Grid>

        <Menu
          anchorEl={columnMenuAnchorEl}
          open={Boolean(columnMenuAnchorEl)}
          onClose={handleCloseColumnMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ px: 2, pt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Colonne aggiuntive
            </Typography>
          </Box>
          <FormGroup sx={{ px: 2, pb: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibility.id}
                  onChange={() => handleToggleColumn('id')}
                />
              }
              label="ID"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibility.responsabile}
                  onChange={() => handleToggleColumn('responsabile')}
                />
              }
              label="Resp. Comm."
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibility.acconto}
                  onChange={() => handleToggleColumn('acconto')}
                />
              }
              label="Acconto"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibility.dataConferimento}
                  onChange={() => handleToggleColumn('dataConferimento')}
                />
              }
              label="Data Conferimento"
            />
          </FormGroup>
        </Menu>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.neutral' }}>
                {columnVisibility.id && (
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'id'}
                      direction={orderBy === 'id' ? order : 'asc'}
                      onClick={() => handleRequestSort('id')}
                    >
                      ID
                    </TableSortLabel>
                  </TableCell>
                )}
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'reference'}
                    direction={orderBy === 'reference' ? order : 'asc'}
                    onClick={() => handleRequestSort('reference')}
                  >
                    Reference
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'rif_customer'}
                    direction={orderBy === 'rif_customer' ? order : 'asc'}
                    onClick={() => handleRequestSort('rif_customer')}
                  >
                    Cliente
                  </TableSortLabel>
                </TableCell>
                {columnVisibility.responsabile && (
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'responsabile_commerciale'}
                      direction={orderBy === 'responsabile_commerciale' ? order : 'asc'}
                      onClick={() => handleRequestSort('responsabile_commerciale')}
                    >
                      Resp. Comm.
                    </TableSortLabel>
                  </TableCell>
                )}
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'importo_totale'}
                    direction={orderBy === 'importo_totale' ? order : 'asc'}
                    onClick={() => handleRequestSort('importo_totale')}
                  >
                    Importo Totale
                  </TableSortLabel>
                </TableCell>
                {columnVisibility.acconto && (
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'acconto'}
                      direction={orderBy === 'acconto' ? order : 'asc'}
                      onClick={() => handleRequestSort('acconto')}
                    >
                      Acconto
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.dataConferimento && (
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'data_conferimento'}
                      direction={orderBy === 'data_conferimento' ? order : 'asc'}
                      onClick={() => handleRequestSort('data_conferimento')}
                    >
                      Data Conferimento
                    </TableSortLabel>
                  </TableCell>
                )}
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'status'}
                    direction={orderBy === 'status' ? order : 'asc'}
                    onClick={() => handleRequestSort('status')}
                  >
                    Stato
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={totalColumns} align="center" sx={{ py: 3 }}>
                    <CircularProgressWithLabel size={50} />
                  </TableCell>
                </TableRow>
              ) : sortedList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={totalColumns} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      Nessun dossier trovato
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedList.map((item) => (
                  <TableRow key={item.id} hover>
                    {columnVisibility.id && <TableCell>{item.id}</TableCell>}
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {item.reference}
                        </Typography>
                        {item.description && (
                          <Typography variant="caption" color="text.secondary" sx={{ 
                            maxWidth: 200, 
                            whiteSpace: 'nowrap', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis' 
                          }}>
                            {item.description}
                          </Typography>
                        )}
                        {item.tipo_contratto && (
                          <Chip 
                            label={item.tipo_contratto} 
                            size="small" 
                            variant="outlined"
                            sx={{ 
                              mt: 0.5, 
                              height: 20, 
                              fontSize: '0.625rem', 
                              width: 'fit-content',
                              borderColor: 'transparent',
                              bgcolor: 'transparent',
                              color: getContractTypeVisuals(item.tipo_contratto).iconColor,
                              fontWeight: 600,
                              px: 0
                            }} 
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {item.customer_ragione_sociale || 
                           ((item.customer_name || item.customer_surname) ? 
                             `${item.customer_name || ''} ${item.customer_surname || ''}`.trim() : 
                             item.rif_customer)}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                          {item.customer_email && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', fontSize: '0.75rem' }}>
                              <Mail size={12} />
                              {item.customer_email}
                            </Box>
                          )}
                          {item.customer_telephone && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', fontSize: '0.75rem' }}>
                              <Phone size={12} />
                              {item.customer_telephone}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    {columnVisibility.responsabile && (
                      <TableCell>
                        {item.responsabile_commerciale ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <User size={16} color="#637381" />
                            <Typography variant="body2">{item.responsabile_commerciale}</Typography>
                          </Box>
                        ) : '-'}
                      </TableCell>
                    )}
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: MONEY_COLOR }}>
                          {item.importo_totale ? `${Number(item.importo_totale).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}` : '-'}
                        </Typography>
                        {item.number_installment > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            {item.number_installment} rate
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    {columnVisibility.acconto && (
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: MONEY_COLOR }}>
                          {item.acconto
                            ? `${Number(item.acconto).toLocaleString('it-IT', {
                                style: 'currency',
                                currency: 'EUR',
                              })}`
                            : '-'}
                        </Typography>
                      </TableCell>
                    )}
                    {columnVisibility.dataConferimento && (
                      <TableCell>
                        {item.data_conferimento
                          ? new Date(item.data_conferimento).toLocaleDateString()
                          : '-'}
                      </TableCell>
                    )}
                    <TableCell>
                      <Chip 
                        label={getDossierStatusLabel(item.status)} 
                        color={getStatusColor(item.status)} 
                        size="small" 
                        variant="outlined" 
                        sx={{ fontWeight: 'bold' }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                        <Tooltip title="Contatti">
                          <IconButton
                            color="info"
                            size="small"
                            onClick={(e) => handleContactsClick(e, item)}
                          >
                            <Users size={18} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Dettaglio Pratiche">
                          <IconButton onClick={() => handleView(item.id)} color="primary">
                            <Eye size={20} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>

      {/* Modal Contatti */}
      <Dialog
        open={openContactsModal}
        onClose={handleCloseContactsModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="h6">
              Contatti associati al Dossier: {selectedDossierRef}
            </Typography>
            {selectedDossierCustomer && (
              <Typography variant="body2" color="text.secondary">
                Cliente: {selectedDossierCustomer.name}
                {selectedDossierCustomer.email && ` · ${selectedDossierCustomer.email}`}
                {selectedDossierCustomer.telephone && ` · ${selectedDossierCustomer.telephone}`}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {loadingContacts ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : selectedContacts.length === 0 ? (
            <Typography align="center" color="text.secondary">
              Nessun contatto trovato per questo dossier.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Nome</TableCell>
                    <TableCell>Cognome</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Telefono</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedContacts.map((contact) => (
                    <TableRow key={contact.id} hover>
                      <TableCell>{contact.id}</TableCell>
                      <TableCell>{contact.nome}</TableCell>
                      <TableCell>{contact.cognome}</TableCell>
                      <TableCell>{contact.email || '-'}</TableCell>
                      <TableCell>{contact.telefono || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseContactsModal}>Chiudi</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
